import React from "react";
import { format } from "date-fns";
import { splitDayDiscount } from "../../utils/milkUtils.js";

export const fmtMoney = (value) =>
  `$${Number(value || 0).toLocaleString("es-VE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

export const fmtLiters = (value) =>
  Number(value || 0).toLocaleString("es-VE", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

export const fmtPrice = (value) =>
  Number(value || 0).toLocaleString("es-VE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  });

export function aggregateWeekResumen(days = [], providers = []) {
  const dailyVaca = {};
  const dailyBufalo = {};
  const dailyDiscount = {};
  days.forEach((day) => {
    dailyVaca[day.date] = 0;
    dailyBufalo[day.date] = 0;
    dailyDiscount[day.date] = 0;
  });

  // Litros de planta = brutos (lo que trajo el proveedor). El descuento solo baja el $.
  let litersVaca = 0;
  let litersBufalo = 0;
  let netLitersVaca = 0;
  let netLitersBufalo = 0;
  let owedVaca = 0;
  let owedBufalo = 0;
  let discountLiters = 0;
  let discountAmount = 0;

  providers.forEach((p) => {
    owedVaca += Number(p.owedVaca) || 0;
    owedBufalo += Number(p.owedBufalo) || 0;
    const priceCow = Number(p.priceCow) || 0;
    const priceBuffalo = Number(p.priceBuffalo) || 0;
    days.forEach((day) => {
      const cell = p.daily?.[day.date] || {};
      const vaca = Number(cell.vaca) || 0;
      const bufalo = Number(cell.bufalo) || 0;
      const discount = Number(cell.discount) || 0;
      dailyVaca[day.date] += vaca;
      dailyBufalo[day.date] += bufalo;
      litersVaca += vaca;
      litersBufalo += bufalo;
      dailyDiscount[day.date] += discount;
      discountLiters += discount;
      const split = splitDayDiscount(vaca, bufalo, discount, cell.discountScope);
      netLitersVaca += split.netVaca;
      netLitersBufalo += split.netBufalo;
      discountAmount += split.vacaDisc * priceCow + split.bufaloDisc * priceBuffalo;
    });
  });

  return {
    dailyVaca,
    dailyBufalo,
    dailyDiscount,
    litersVaca,
    litersBufalo,
    discountLiters,
    discountAmount,
    owedVaca,
    owedBufalo,
    priceCow: netLitersVaca > 0 ? owedVaca / netLitersVaca : 0,
    priceBuffalo: netLitersBufalo > 0 ? owedBufalo / netLitersBufalo : 0,
    totalLiters: litersVaca + litersBufalo,
    totalOwed: owedVaca + owedBufalo,
  };
}

export default function MilkWeekResumenTable({
  days = [],
  dailyVaca = {},
  dailyBufalo = {},
  dailyDiscount = {},
  litersVaca = 0,
  litersBufalo = 0,
  discountLiters = 0,
  priceCow = 0,
  priceBuffalo = 0,
  owedVaca = 0,
  owedBufalo = 0,
  discountAmount = 0,
  hidePrices = false,
}) {
  const totalLiters = Number(litersVaca) + Number(litersBufalo);
  const totalOwed = Number(owedVaca) + Number(owedBufalo);

  return (
    <div className="overflow-x-auto border border-gray-200 rounded-lg bg-white">
      <table className="w-full text-sm min-w-[720px]">
        <thead>
          <tr className="bg-[#e7efe9] text-gray-800">
            <th className="px-2 py-2 text-left font-semibold w-24">Resumen</th>
            {days.map((day) => (
              <th key={day.date} className="px-1 py-2 text-center font-semibold min-w-[4.5rem]">
                <div className="whitespace-pre-line leading-tight">
                  {day.shortLabel || day.label}
                  {"\n"}
                  {format(new Date(`${day.date}T12:00:00`), "dd/MM")}
                </div>
              </th>
            ))}
            <th className="px-2 py-2 text-right font-semibold whitespace-nowrap">Total L</th>
            {!hidePrices && (
              <>
                <th className="px-2 py-2 text-right font-semibold whitespace-nowrap">Precio</th>
                <th className="px-2 py-2 text-right font-semibold whitespace-nowrap">$</th>
              </>
            )}
          </tr>
        </thead>
        <tbody>
          <tr className="border-t border-gray-100">
            <td className="px-2 py-2 font-medium text-gray-800">Vaca</td>
            {days.map((day) => (
              <td key={`vaca-${day.date}`} className="px-1 py-2 text-center tabular-nums text-gray-800">
                {fmtLiters(dailyVaca[day.date])}
              </td>
            ))}
            <td className="px-2 py-2 text-right font-semibold tabular-nums">{fmtLiters(litersVaca)}</td>
            {!hidePrices && (
              <>
                <td className="px-2 py-2 text-right tabular-nums text-gray-700">{fmtPrice(priceCow)}</td>
                <td className="px-2 py-2 text-right font-semibold tabular-nums">{fmtMoney(owedVaca)}</td>
              </>
            )}
          </tr>
          <tr className="border-t border-gray-100 bg-gray-50/60">
            <td className="px-2 py-2 font-medium text-gray-800">Búfala</td>
            {days.map((day) => (
              <td key={`bufalo-${day.date}`} className="px-1 py-2 text-center tabular-nums text-gray-800">
                {fmtLiters(dailyBufalo[day.date])}
              </td>
            ))}
            <td className="px-2 py-2 text-right font-semibold tabular-nums">{fmtLiters(litersBufalo)}</td>
            {!hidePrices && (
              <>
                <td className="px-2 py-2 text-right tabular-nums text-gray-700">{fmtPrice(priceBuffalo)}</td>
                <td className="px-2 py-2 text-right font-semibold tabular-nums">{fmtMoney(owedBufalo)}</td>
              </>
            )}
          </tr>
          <tr className="border-t border-amber-100 bg-amber-50/70">
            <td className="px-2 py-2 font-medium text-amber-800">Descuento</td>
            {days.map((day) => (
              <td key={`discount-${day.date}`} className="px-1 py-2 text-center tabular-nums text-amber-800">
                {fmtLiters(dailyDiscount[day.date])}
              </td>
            ))}
            <td className="px-2 py-2 text-right font-semibold tabular-nums text-amber-800">
              {fmtLiters(discountLiters)}
            </td>
            {!hidePrices && (
              <>
                <td className="px-2 py-2 text-right text-xs font-medium text-amber-700">Agua</td>
                <td className="px-2 py-2 text-right font-semibold tabular-nums text-amber-800">
                  {Number(discountAmount) > 0 ? `−${fmtMoney(discountAmount)}` : fmtMoney(0)}
                </td>
              </>
            )}
          </tr>
          <tr className="border-t border-gray-200 bg-[#e7efe9]/70">
            <td className="px-2 py-2 font-semibold text-gray-900">Total L</td>
            {days.map((day) => (
              <td key={`total-${day.date}`} className="px-1 py-2 text-center font-semibold tabular-nums">
                {fmtLiters((Number(dailyVaca[day.date]) || 0) + (Number(dailyBufalo[day.date]) || 0))}
              </td>
            ))}
            <td className="px-2 py-2 text-right font-semibold tabular-nums">{fmtLiters(totalLiters)}</td>
            {!hidePrices && (
              <>
                <td className="px-2 py-2 text-right text-xs font-medium text-gray-600 whitespace-nowrap">
                  Total $
                </td>
                <td className="px-2 py-2 text-right font-semibold tabular-nums">{fmtMoney(totalOwed)}</td>
              </>
            )}
          </tr>
        </tbody>
      </table>
    </div>
  );
}
